/**
 * Payment Routing Service
 *
 * PHASE 1.2: Platform Detection & Payment Method Routing (iOS-First Approach)
 *
 * This service is the core of our cross-platform payment strategy, automatically
 * routing payment requests to the appropriate payment system based on platform:
 *
 * ROUTING LOGIC:
 * - iOS native app (Capacitor) → iOS In-App Purchases (IAP) via cordova-plugin-purchase
 * - Android native app (Capacitor) → Stripe (Phase 6 will add Android IAP)
 * - Web browser (mobile/desktop) → Stripe
 *
 * FEATURES:
 * - Platform isolation: iOS IAP code only runs on iOS native apps
 * - Feature flags: Simple boolean controls for payment method availability
 * - Unified interface: Single API for all payment operations
 * - Error handling: Comprehensive error handling and logging
 * - Backward compatibility: Existing Stripe flow remains unchanged
 *
 * INTEGRATION:
 * - Integrates with existing permissions system via usePermissionsAPI
 * - Uses existing Stripe checkout session creation
 * - Leverages iOS IAP service from Phase 1.1
 * - Maintains all existing subscription management functionality
 */

import { isIOSNativeApp, getPlatform, isNativeApp } from '@/utils/platformUtils';
import { iosIAPService } from '@/services/iap/IOSInAppPurchaseService';
import { productConfigurationService } from '@/services/iap/ProductConfigurationService';
import { unifiedProductDiscoveryService } from './UnifiedProductDiscoveryService';

// Feature flags for payment methods
interface PaymentFeatureFlags {
  readonly iosIAPEnabled: boolean;
  readonly androidIAPEnabled: boolean; // For future Phase 6
}

// Payment method types
export type PaymentMethod = 'ios_iap' | 'stripe';

// Payment request interface
export interface PaymentRequest {
  readonly priceType: 'monthly' | 'annual';
  readonly referralCode?: string;
}

// Payment result interface
export interface PaymentResult {
  readonly success: boolean;
  readonly method: PaymentMethod;
  readonly error?: string;
  readonly transactionId?: string;
  readonly productId?: string;
  readonly receipt?: string;
  readonly metadata?: Record<string, unknown>;
}

// Subscription sync callback for integration with existing subscription system
export type SubscriptionSyncCallback = (subscriptionData: {
  success: boolean;
  method: PaymentMethod;
  productId?: string;
  transactionId?: string;
  receipt?: string;
  isActive: boolean;
  error?: string;
}) => Promise<void>;

/**
 * Payment Routing Service
 * Determines which payment system to use based on platform and feature flags
 */
export class PaymentRoutingService {
  private readonly featureFlags: PaymentFeatureFlags;
  private subscriptionSyncCallback?: SubscriptionSyncCallback;

  constructor() {
    // Simple boolean feature flags for Phase 1.2
    this.featureFlags = {
      iosIAPEnabled: true, // Enable iOS IAP for testing
      androidIAPEnabled: false // Phase 6 feature
    };

    console.log('💳 Payment Routing Service: Initialized with flags:', this.featureFlags);

    // Set up iOS IAP integration callbacks
    this.setupIOSIAPIntegration();

    // Initialize iOS IAP service automatically on iOS native apps
    if (isIOSNativeApp() && this.featureFlags.iosIAPEnabled) {
      console.log('🔧 Payment Routing: Auto-initializing iOS IAP service...');
      iosIAPService.initialize().then((success) => {
        if (success) {
          console.log('✅ Payment Routing: iOS IAP service auto-initialized successfully');
        } else {
          console.log('⚠️ Payment Routing: iOS IAP service auto-initialization failed');
        }
      }).catch((error) => {
        console.error('❌ Payment Routing: iOS IAP service auto-initialization error:', error);
      });
    }
  }

  /**
   * Set subscription sync callback for integration with existing subscription system
   */
  setSubscriptionSyncCallback(callback: SubscriptionSyncCallback): void {
    this.subscriptionSyncCallback = callback;
    console.log('🔗 Payment Routing: Subscription sync callback registered');
  }

  /**
   * Set up iOS IAP integration with existing subscription system
   */
  private setupIOSIAPIntegration(): void {
    if (!isIOSNativeApp()) {
      return;
    }

    // Set up purchase completion callback
    iosIAPService.setPurchaseCompletionCallback(async (result) => {
      console.log('🔗 Payment Routing: iOS IAP purchase completion callback triggered:', result);

      if (this.subscriptionSyncCallback) {
        await this.subscriptionSyncCallback({
          success: result.success,
          method: 'ios_iap',
          productId: result.productId,
          transactionId: result.transactionId,
          receipt: result.receipt,
          isActive: result.success,
          error: result.error
        });
      }
    });

    // Set up subscription sync callback (V2 service has simplified callback)
    iosIAPService.setSubscriptionSyncCallback(async () => {
      console.log('🔗 Payment Routing: iOS IAP subscription sync callback triggered (V2 simplified)');

      if (this.subscriptionSyncCallback) {
        await this.subscriptionSyncCallback({
          success: true,
          method: 'ios_iap',
          productId: 'unknown', // V2 service doesn't provide detailed data
          transactionId: undefined,
          receipt: undefined,
          isActive: true
        });
      }
    });

    console.log('🔗 Payment Routing: iOS IAP integration callbacks configured');
  }

  /**
   * Set current user ID for comprehensive payment service database integration
   *
   * PHASE 2.2 ENHANCEMENT: This method now coordinates user ID setting across
   * all payment-related services to enable comprehensive database integration,
   * analytics, and cross-platform subscription management.
   *
   * Services Updated:
   * - IOSInAppPurchaseService: For iOS IAP transaction persistence
   * - UnifiedProductDiscoveryService: For subscription analytics and history
   *
   * Database Integration Benefits:
   * - Cross-platform transaction tracking and correlation
   * - Unified subscription status across all platforms
   * - Purchase history and analytics for business intelligence
   * - User-specific product recommendations and pricing
   * - Comprehensive error tracking and debugging
   *
   * @param userId - The authenticated user's ID from Supabase Auth
   */
  setCurrentUserId(userId: string): void {
    console.log('👤 Payment Routing: Setting current user ID for payment services:', userId);

    // Set user ID for iOS IAP service (iOS native apps only)
    // This enables database persistence for iOS IAP transactions
    if (isIOSNativeApp()) {
      console.log('📱 Payment Routing: Setting user ID for iOS IAP service');
      iosIAPService.setCurrentUserId(userId);
    }

    // Set user ID for unified product discovery (all platforms)
    // This enables subscription analytics and transaction history
    console.log('🔍 Payment Routing: Setting user ID for unified product discovery');
    unifiedProductDiscoveryService.setCurrentUserId(userId);
  }

  /**
   * Determine which payment method to use based on platform and feature flags
   */
  getPaymentMethod(): PaymentMethod {
    const platform = getPlatform();
    const isNative = isNativeApp();
    const isIOSNative = isIOSNativeApp();

    console.log('🔍 Payment Routing: Platform detection:', {
      platform,
      isNative,
      isIOSNative,
      featureFlags: this.featureFlags
    });

    // iOS native app with IAP enabled
    if (isIOSNative && this.featureFlags.iosIAPEnabled) {
      console.log('📱 Payment Routing: Using iOS IAP');
      return 'ios_iap';
    }

    // All other cases use Stripe
    console.log('💳 Payment Routing: Using Stripe');
    return 'stripe';
  }

  /**
   * Check if payments are available on current platform
   */
  async isPaymentAvailable(): Promise<boolean> {
    const method = this.getPaymentMethod();

    if (method === 'ios_iap') {
      // For iOS IAP, check if it's actually available and initialized
      const available = await this.isIOSIAPAvailable();
      console.log(`📱 Payment Routing: iOS IAP availability check: ${available}`);
      return available;
    }

    if (method === 'stripe') {
      // For Stripe, check if we're in a web environment
      const isWeb = !isNativeApp();
      console.log(`💳 Payment Routing: Stripe availability check: ${isWeb} (web environment required)`);
      return isWeb;
    }

    return false;
  }

  /**
   * Check if iOS IAP is available and ready
   */
  async isIOSIAPAvailable(): Promise<boolean> {
    if (!this.featureFlags.iosIAPEnabled) {
      console.log('🚫 Payment Routing: iOS IAP disabled by feature flag');
      return false;
    }

    if (!isIOSNativeApp()) {
      console.log('🚫 Payment Routing: Not iOS native app');
      return false;
    }

    // Initialize IAP service if not already done
    if (!iosIAPService.isAvailable()) {
      console.log('🔧 Payment Routing: Initializing iOS IAP service...');
      const initialized = await iosIAPService.initialize();
      if (!initialized) {
        console.log('❌ Payment Routing: iOS IAP initialization failed');
        return false;
      }
    }

    const available = iosIAPService.isAvailable();
    console.log(`${available ? '✅' : '❌'} Payment Routing: iOS IAP availability:`, available);
    return available;
  }

  /**
   * Route payment request to appropriate payment system
   */
  async initiatePayment(request: PaymentRequest): Promise<PaymentResult> {
    const method = this.getPaymentMethod();

    console.log('🚀 Payment Routing: Initiating payment:', {
      method,
      priceType: request.priceType,
      hasReferralCode: !!request.referralCode
    });

    // Check if payment is available on current platform
    const available = await this.isPaymentAvailable();
    if (!available) {
      const errorMessage = method === 'ios_iap'
        ? 'iOS In-App Purchases are not available yet. Please try again later or contact support.'
        : 'Payments are not available in this app version. Please use the web version.';

      console.error('❌ Payment Routing: Payment not available on current platform');
      return {
        success: false,
        method,
        error: errorMessage
      };
    }

    try {
      switch (method) {
        case 'ios_iap':
          return await this.initiateIOSIAP(request);

        case 'stripe':
          return await this.initiateStripe(request);

        default:
          throw new Error(`Unsupported payment method: ${method}`);
      }
    } catch (error) {
      console.error('❌ Payment Routing: Payment initiation failed:', error);
      return {
        success: false,
        method,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Initiate iOS In-App Purchase using unified product discovery
   */
  private async initiateIOSIAP(request: PaymentRequest): Promise<PaymentResult> {
    console.log('📱 Payment Routing: Initiating iOS IAP...');

    try {
      // Check availability first
      const available = await this.isIOSIAPAvailable();
      if (!available) {
        throw new Error('iOS IAP not available');
      }

      // Use unified product discovery to get the product
      const interval = request.priceType === 'annual' ? 'annual' : 'monthly';
      const product = await unifiedProductDiscoveryService.getProduct(
        `premium_${interval}`,
        { platform: 'ios_iap', interval }
      );

      if (!product || !product.iosData?.productId) {
        throw new Error(`No iOS product found for ${interval} subscription`);
      }

      const productId = product.iosData.productId;
      console.log(`🛒 Payment Routing: Purchasing iOS product: ${productId} (${interval})`);

      // PHASE 2.2 ENHANCEMENT: Comprehensive analytics logging for purchase initiation
      // This data is used for business intelligence, conversion tracking, and debugging
      console.log('📊 Payment Routing: iOS IAP purchase initiated:', {
        unifiedProductId: product.id,
        iosProductId: productId,
        interval,
        priceInCents: product.priceInCents,
        formattedPrice: product.formattedPrice,
        databaseIntegration: product.databaseIntegration, // User's subscription history
        timestamp: new Date().toISOString()
      });

      // Initiate the actual iOS IAP purchase through cordova-plugin-purchase
      // The IOSInAppPurchaseService handles:
      // - Product validation and availability checking
      // - Transaction state management and retry logic
      // - Database persistence for cross-platform tracking
      // - Receipt handling and validation preparation
      const result = await iosIAPService.purchaseProduct(productId);

      // PHASE 2.2 ENHANCEMENT: Enhanced result logging with comprehensive metadata
      // This data supports analytics, debugging, and business intelligence
      console.log('📊 Payment Routing: iOS IAP purchase result:', {
        success: result.success,
        productId: result.productId,
        transactionId: result.transactionId,
        error: result.error,
        timestamp: new Date().toISOString()
      });

      return {
        success: result.success,
        method: 'ios_iap',
        error: result.error,
        transactionId: result.transactionId,
        productId: result.productId,
        receipt: result.receipt,
        metadata: {
          unifiedProductId: product.id,
          interval,
          priceInCents: product.priceInCents,
          databaseIntegration: product.databaseIntegration
        }
      };
    } catch (error) {
      console.error('❌ Payment Routing: iOS IAP failed:', error);

      // Log the error for analytics and debugging
      const errorMessage = error instanceof Error ? error.message : 'iOS IAP failed';
      console.error('💾 Payment Routing: Logging iOS IAP failure for analytics:', {
        interval: request.priceType,
        error: errorMessage,
        timestamp: new Date().toISOString()
      });

      return {
        success: false,
        method: 'ios_iap',
        error: errorMessage,
        metadata: {
          failureReason: 'ios_iap_service_error',
          interval: request.priceType
        }
      };
    }
  }

  /**
   * Initiate Stripe payment (existing flow)
   */
  private async initiateStripe(request: PaymentRequest): Promise<PaymentResult> {
    console.log('💳 Payment Routing: Initiating Stripe payment...');

    // Import the existing Stripe payment logic
    const { supabase } = await import('@/integrations/supabase/client');
    
    // Get current session for authentication
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.access_token) {
      throw new Error('User not authenticated');
    }

    // Call existing Stripe checkout session creation
    const requestBody: { priceType: 'monthly' | 'annual'; referralCode?: string } = { 
      priceType: request.priceType 
    };
    
    if (request.referralCode) {
      requestBody.referralCode = request.referralCode;
    }

    const { data, error } = await supabase.functions.invoke('create-checkout-session', {
      method: 'POST',
      body: JSON.stringify(requestBody)
    });

    if (error) {
      console.error('💳 Payment Routing: Stripe error:', error);
      throw error;
    }

    if (!data?.url) {
      console.error('💳 Payment Routing: No checkout URL in response:', data);
      throw new Error('No checkout URL returned');
    }

    // Redirect to Stripe checkout
    console.log('💳 Payment Routing: Redirecting to Stripe checkout...');
    window.location.href = data.url;

    return {
      success: true,
      method: 'stripe'
    };
  }

  /**
   * Get transaction state for iOS IAP (if available)
   */
  getTransactionState(_productId: string): unknown {
    if (this.getPaymentMethod() === 'ios_iap') {
      return iosIAPService.getTransactionState(); // V2 service simplified interface
    }
    return undefined;
  }

  /**
   * Get all transaction states for iOS IAP (if available)
   */
  getAllTransactionStates(): Map<string, unknown> | undefined {
    if (this.getPaymentMethod() === 'ios_iap') {
      return iosIAPService.getAllTransactionStates();
    }
    return undefined;
  }

  /**
   * Clear completed transactions (cleanup for iOS IAP)
   */
  clearCompletedTransactions(): void {
    if (this.getPaymentMethod() === 'ios_iap') {
      iosIAPService.clearCompletedTransactions();
    }
  }

  /**
   * Restore purchases (iOS IAP only)
   */
  async restorePurchases(): Promise<boolean> {
    if (this.getPaymentMethod() === 'ios_iap') {
      return await iosIAPService.restorePurchases();
    }
    return false;
  }

  /**
   * Get available products using unified product discovery
   */
  async getAvailableProducts(): Promise<readonly unknown[]> {
    const result = await unifiedProductDiscoveryService.discoverProducts();
    return result.products;
  }

  /**
   * Get platform information for debugging
   */
  getPlatformInfo(): Record<string, unknown> {
    const baseInfo = {
      platform: getPlatform(),
      isNativeApp: isNativeApp(),
      isIOSNativeApp: isIOSNativeApp(),
      paymentMethod: this.getPaymentMethod(),
      featureFlags: this.featureFlags,
      iosIAPAvailable: iosIAPService.isAvailable(),
      productConfiguration: productConfigurationService.getDebugInfo(),
      unifiedProductDiscovery: unifiedProductDiscoveryService.getDebugInfo(),
      subscriptionSyncCallbackRegistered: !!this.subscriptionSyncCallback
    };

    // Add iOS IAP specific debug info if available
    if (this.getPaymentMethod() === 'ios_iap') {
      return {
        ...baseInfo,
        iosIAPDebugInfo: iosIAPService.getPlatformInfo(),
        transactionStates: Array.from(iosIAPService.getAllTransactionStates().entries())
      };
    }

    return baseInfo;
  }

  /**
   * Update feature flags (for testing/admin control)
   */
  updateFeatureFlags(flags: Partial<PaymentFeatureFlags>): void {
    Object.assign(this.featureFlags, flags);
    console.log('🔧 Payment Routing: Feature flags updated:', this.featureFlags);
  }
}

// Export singleton instance
export const paymentRoutingService = new PaymentRoutingService();
